import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { GROUP_ADMIN_ROLE_CODES, INVITATION_TYPES } from '@/types/platform';
import type {
  AdministratorInvitation,
  InstitutionAdministratorsResponse,
  InvitationType,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * The administrators tab — the people who run the group, not the institutions.
 *
 * GROUP INVITATIONS SHARE THE INSTITUTION SURFACE. Identity never grew a second
 * invitation endpoint for groups: InviteAdministratorDto gained an optional
 * groupId, and the same routes serve both scopes. The invitation is for a seat
 * at the group — GROUP_ADMINISTRATOR, GROUP_FINANCE_MANAGER and the rest are
 * the contract names identity accepts for exactly this — and the listing
 * surfaces groupName/groupType so a group-scoped row is never mistaken for an
 * institution one.
 *
 * THE TYPES ARE STILL IDENTITY'S VOCABULARY. PRIMARY_ADMINISTRATOR and
 * SECONDARY_ADMINISTRATOR describe how the seat is held, and the console offers
 * both the way it does for institutions — they are the two types the listing
 * shows, and an invitation type this console cannot display afterwards is not
 * one worth offering.
 */

function invitationTypeLabel(type: InvitationType, t: ReturnType<typeof useTranslation>['t']): string {
  return t(`platform.admins.invitationType.${type}`, { defaultValue: type });
}

function roleLabel(name: string, t: ReturnType<typeof useTranslation>['t']): string {
  return t(`platform.groups.admins.role.${name}`, { defaultValue: name });
}

/** Invitations that need someone to act, before those that do not. */
function byUrgency(a: AdministratorInvitation, b: AdministratorInvitation): number {
  const rank = (invitation: AdministratorInvitation) =>
    invitation.status === 'FAILED' ? 0 : invitation.status === 'EXPIRED' ? 1 : invitation.status === 'PENDING' ? 2 : 3;
  return rank(a) - rank(b);
}

export function GroupAdministratorsPanel({ groupId, groupName }: { groupId: string; groupName: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.administrators.list(undefined, groupId), [groupId]);
  const { state, reload, replace } = useAsyncData<InstitutionAdministratorsResponse>(
    `group-admins:${groupId}`,
    fetcher,
    groupId.length > 0,
  );

  const [inviteOpen, setInviteOpen] = useState(false);
  const [revoking, setRevoking] = useState<AdministratorInvitation | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const applyInvitation = (updated: AdministratorInvitation) => {
    if (state.status !== 'ready') return;
    const existing = state.data.invitations ?? [];
    replace({
      ...state.data,
      invitations: existing.some((entry) => entry.id === updated.id)
        ? existing.map((entry) => (entry.id === updated.id ? updated : entry))
        : [...existing, updated],
    });
  };

  const resend = async (invitation: AdministratorInvitation) => {
    setBusyId(invitation.id);
    setRowError(null);
    try {
      applyInvitation(await platformApi.administrators.resend(invitation.id));
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invitation: AdministratorInvitation, reason: string) => {
    setBusyId(invitation.id);
    setRowError(null);
    try {
      applyInvitation(await platformApi.administrators.revoke(invitation.id, reason));
      setRevoking(null);
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Panel
        title={t('platform.groups.admins.title')}
        icon="users"
        actions={
          <button type="button" className={styles.primaryBtnSm} onClick={() => setInviteOpen(true)}>
            <Icon name="mail" size={15} />
            {t('platform.groups.admins.invite')}
          </button>
        }
      >
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => (data.invitations?.length ?? 0) === 0}
          emptyTitle={t('platform.groups.admins.emptyTitle')}
          emptyBody={t('platform.groups.admins.emptyBody')}
        >
          {(data) => {
            const invitations = data.invitations ?? [];
            const pending = invitations.filter((invitation) => invitation.status !== 'ACCEPTED');
            const active = invitations.filter((invitation) => invitation.status === 'ACCEPTED');

            return (
              <>
                {pending.length > 0 && (
                  <>
                    <h3 className={styles.subheading}>{t('platform.groups.admins.invitations')}</h3>
                    <ul className={styles.rowList}>
                      {[...pending].sort(byUrgency).map((invitation) => (
                        <li key={invitation.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <p className={styles.rowTitle}>{invitation.email}</p>
                            <p className={styles.rowMetaLine}>
                              {invitationTypeLabel(invitation.invitationType, t)}
                              {invitation.initialRoleNames.length > 0
                                ? ` · ${invitation.initialRoleNames.map((name) => roleLabel(name, t)).join(', ')}`
                                : ''}
                              {invitation.lastSentAt
                                ? ` · ${t('platform.groups.admins.sent', {
                                    when: formatRelative(invitation.lastSentAt, locale),
                                  })}`
                                : ''}
                              {invitation.expiresAt
                                ? ` · ${t('platform.groups.admins.expires', {
                                    when: formatDateTime(invitation.expiresAt, locale),
                                  })}`
                                : ''}
                              {invitation.resendCount
                                ? ` · ${t('platform.groups.admins.resent', { n: invitation.resendCount })}`
                                : ''}
                            </p>
                          </div>
                          <div className={styles.rowAside}>
                            <StateBadge kind="invitation" value={invitation.status} size="sm" />
                            <button
                              type="button"
                              className={styles.linkBtn}
                              disabled={busyId === invitation.id}
                              onClick={() => resend(invitation)}
                            >
                              {t('platform.groups.admins.resend')}
                            </button>
                            {(invitation.status === 'PENDING' || invitation.status === 'FAILED') && (
                              <button
                                type="button"
                                className={styles.linkBtnDanger}
                                disabled={busyId === invitation.id}
                                onClick={() => setRevoking(invitation)}
                              >
                                {t('platform.groups.admins.revoke')}
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {active.length > 0 && (
                  <>
                    <h3 className={styles.subheading}>{t('platform.groups.admins.active')}</h3>
                    <ul className={styles.rowList}>
                      {active.map((invitation) => (
                        <li key={invitation.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <p className={styles.rowTitle}>{invitation.email}</p>
                            <p className={styles.rowMetaLine}>
                              {invitationTypeLabel(invitation.invitationType, t)}
                              {invitation.initialRoleNames.length > 0
                                ? ` · ${invitation.initialRoleNames.map((name) => roleLabel(name, t)).join(', ')}`
                                : ''}
                              {invitation.acceptedAt
                                ? ` · ${t('platform.groups.admins.accepted', {
                                    when: formatRelative(invitation.acceptedAt, locale),
                                  })}`
                                : ''}
                            </p>
                          </div>
                          <div className={styles.rowAside}>
                            <StateBadge kind="invitation" value={invitation.status} size="sm" />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {rowError && (
                  <p className={styles.formError} role="alert">
                    {rowError}
                  </p>
                )}
              </>
            );
          }}
        </AsyncPanel>
      </Panel>

      {revoking && (
        <ConfirmActionDialog
          open
          title={t('platform.groups.admins.revokeTitle')}
          intro={t('platform.groups.admins.revokeIntro', { email: revoking.email })}
          tone="warning"
          impactStatus="ready"
          impact={
            <ul className={styles.impactList}>
              <li>{t('platform.groups.admins.revokeImpactLink')}</li>
              <li>{t('platform.groups.admins.revokeImpactStalled')}</li>
              <li>{t('platform.groups.admins.revokeImpactAudit')}</li>
            </ul>
          }
          confirmLabel={t('platform.groups.admins.revoke')}
          submitting={busyId === revoking.id}
          error={rowError}
          onCancel={() => setRevoking(null)}
          onConfirm={(reason) => revoke(revoking, reason)}
        />
      )}

      <InviteDialog
        open={inviteOpen}
        groupId={groupId}
        groupName={groupName}
        onClose={() => setInviteOpen(false)}
        onInvited={(invitation) => {
          applyInvitation(invitation);
          setInviteOpen(false);
        }}
      />
    </>
  );
}

function InviteDialog({
  open,
  groupId,
  groupName,
  onClose,
  onInvited,
}: {
  open: boolean;
  groupId: string;
  groupName: string;
  onClose: () => void;
  onInvited: (invitation: AdministratorInvitation) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [invitationType, setInvitationType] = useState<InvitationType>(INVITATION_TYPES[0]);
  const [roleName, setRoleName] = useState<string>(GROUP_ADMIN_ROLE_CODES[0] as string);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const valid = emailValid && firstName.trim().length > 0 && lastName.trim().length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const invitation = await platformApi.administrators.invite(
        undefined,
        {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          invitationType,
          initialRoleNames: [roleName],
        },
        groupId,
      );
      setEmail('');
      setFirstName('');
      setLastName('');
      onInvited(invitation);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.groups.admins.inviteTitle')}
      description={t('platform.groups.admins.inviteLead', { name: groupName })}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldEmail')}</span>
          <input
            className={styles.input}
            type="email"
            value={email}
            required
            aria-invalid={email.length > 0 && !emailValid}
            aria-describedby="group-invite-email-hint"
            onChange={(event) => setEmail(event.target.value)}
          />
          <span className={styles.fieldHint} id="group-invite-email-hint">
            {t('platform.groups.admins.fieldEmailHint')}
          </span>
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldFirstName')}</span>
            <input
              className={styles.input}
              value={firstName}
              required
              onChange={(event) => setFirstName(event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldLastName')}</span>
            <input
              className={styles.input}
              value={lastName}
              required
              onChange={(event) => setLastName(event.target.value)}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldPhone')}</span>
          <input className={styles.input} type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldType')}</span>
          <Select
            value={invitationType}
            onChange={(value) => setInvitationType(value as InvitationType)}
            options={INVITATION_TYPES.filter((type) => type !== 'STANDARD').map((type) => ({
              value: type,
              label: invitationTypeLabel(type, t),
            }))}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.admins.fieldRole')}</span>
          <Select
            value={roleName}
            onChange={setRoleName}
            options={GROUP_ADMIN_ROLE_CODES.map((code) => ({ value: code, label: roleLabel(code, t) }))}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.groups.admins.inviting') : t('platform.groups.admins.invite')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
